import DOMPurify from 'dompurify'
import { marked } from 'marked'
import { type Component, createMemo, Show, splitProps } from 'solid-js'
import { twMerge } from 'tailwind-merge'
import type { MarkdownPropsType } from '~/types/Markdown'

/**
 * Lo que un modelo escribe de verdad. Todo lo que no esté aquí se elimina, y la
 * lista es explícita a propósito: es una allowlist, así que una etiqueta nueva
 * y peligrosa no entra por olvido.
 *
 * Fuera quedan, entre otras: <img> (una URL remota en la respuesta filtraría la
 * IP del visitante al servidor que la sirva), <iframe>, <style> y <form>.
 */
const ALLOWED_TAGS = [
  'p',
  'br',
  'hr',
  'span',
  'div',
  'strong',
  'b',
  'em',
  'i',
  'del',
  's',
  'mark',
  'ul',
  'ol',
  'li',
  'blockquote',
  'code',
  'pre',
  'a',
  'h1',
  'h2',
  'h3',
  'h4',
  'h5',
  'h6',
  'table',
  'thead',
  'tbody',
  'tr',
  'th',
  'td'
]

const ALLOWED_ATTR = ['href', 'title', 'class', 'target', 'rel', 'align']

/**
 * El markdown lo escribe un MODELO, y en el widget público lo que el modelo
 * escribe lo influye un visitante anónimo. O sea: es contenido no confiable que
 * acaba en innerHTML, que es la definición de un XSS si no se sanea. Por eso el
 * saneado vive aquí dentro y no en quien llama: no se puede olvidar.
 */
let hooksReady = false

function ensureHooks() {
  if (hooksReady) return
  hooksReady = true

  DOMPurify.addHook('afterSanitizeAttributes', node => {
    if (node.tagName !== 'A') return

    // Un enlace del chat lleva a un sitio ajeno: se abre aparte, sin dar acceso
    // a nuestra ventana (noopener) ni filtrar de dónde viene (noreferrer).
    node.setAttribute('target', '_blank')
    node.setAttribute('rel', 'noopener noreferrer nofollow')

    // DOMPurify ya bloquea javascript:, pero un href que no sobrevive al saneado
    // deja un <a> sin destino que parece pulsable y no hace nada.
    if (!node.getAttribute('href')) {
      node.removeAttribute('target')
      node.removeAttribute('rel')
    }
  })
}

const Markdown: Component<MarkdownPropsType> = props => {
  const [local, rest] = splitProps(props, ['content', 'headings', 'class'])

  /**
   * DOMPurify se desactiva solo si no reconoce el entorno, y entonces sanitize()
   * DEVUELVE LA ENTRADA SIN TOCAR: falla en abierto. En un navegador siempre lo
   * reconoce, así que esto no debería pasar nunca; pero si pasara, inyectaríamos
   * HTML del modelo sin filtrar, que es justo lo que este componente evita.
   * Ante la duda, texto plano: se ve peor, no compromete a nadie.
   */
  const canSanitize = () => DOMPurify.isSupported

  const html = createMemo(() => {
    if (!canSanitize()) return ''
    ensureHooks()

    // `breaks`: los modelos separan líneas con un solo salto y esperan verlo.
    // `gfm`: tablas, tachado y autoenlaces, que es lo que escriben.
    const parsed = marked.parse(local.content ?? '', { async: false, breaks: true, gfm: true }) as string

    return DOMPurify.sanitize(parsed, {
      ALLOWED_TAGS,
      ALLOWED_ATTR,
      // Sin esto, <a href="data:text/html,..."> pasaría el filtro.
      ALLOW_DATA_ATTR: false
    })
  })

  const classes = createMemo(() =>
    twMerge(
      'text-sm leading-relaxed break-words',
      // Los márgenes van aquí y no con `prose` de Tailwind Typography porque el
      // proyecto no lo trae y no vale la pena otra dependencia por esto.
      '[&_p]:my-2 [&_p:first-child]:mt-0 [&_p:last-child]:mb-0',
      '[&_ul]:my-2 [&_ul]:ps-5 [&_ul]:list-disc [&_ol]:my-2 [&_ol]:ps-5 [&_ol]:list-decimal [&_li]:my-0.5',
      '[&_strong]:font-semibold [&_em]:italic [&_del]:line-through',
      '[&_a]:underline [&_a]:underline-offset-2 [&_a:hover]:opacity-80',
      '[&_blockquote]:border-s-2 [&_blockquote]:border-current/30 [&_blockquote]:ps-3 [&_blockquote]:my-2 [&_blockquote]:opacity-90',
      '[&_code]:rounded [&_code]:bg-black/20 [&_code]:px-1 [&_code]:py-0.5 [&_code]:text-[0.85em] [&_code]:font-mono',
      // El bloque de código no debe ensanchar la burbuja: scroll dentro de él.
      '[&_pre]:my-2 [&_pre]:overflow-x-auto [&_pre]:rounded-lg [&_pre]:bg-black/30 [&_pre]:p-3',
      '[&_pre_code]:bg-transparent [&_pre_code]:p-0',
      // Una tabla ancha tampoco: el contenedor scrollea, la burbuja no crece.
      '[&_table]:my-2 [&_table]:block [&_table]:overflow-x-auto [&_table]:w-full [&_table]:text-xs',
      '[&_th]:border [&_th]:border-current/20 [&_th]:px-2 [&_th]:py-1 [&_th]:text-start [&_th]:font-semibold',
      '[&_td]:border [&_td]:border-current/20 [&_td]:px-2 [&_td]:py-1',
      '[&_hr]:my-3 [&_hr]:border-current/20',
      local.headings
        ? '[&_h1]:text-lg [&_h1]:font-semibold [&_h1]:my-2 [&_h2]:text-base [&_h2]:font-semibold [&_h2]:my-2 [&_h3]:font-semibold [&_h3]:my-2 [&_h4]:font-semibold [&_h4]:my-2'
        : // En una burbuja, un h1 a tamaño título grita. Se ven como negrita.
          '[&_h1]:text-sm [&_h1]:font-semibold [&_h1]:my-2 [&_h2]:text-sm [&_h2]:font-semibold [&_h2]:my-2 [&_h3]:text-sm [&_h3]:font-semibold [&_h3]:my-2 [&_h4]:text-sm [&_h4]:font-semibold [&_h4]:my-2 [&_h5]:text-sm [&_h5]:font-semibold [&_h6]:text-sm [&_h6]:font-semibold',
      local.class || ''
    )
  )

  return (
    <Show when={canSanitize()} fallback={<div {...rest} class={twMerge(classes(), 'whitespace-pre-wrap')} textContent={local.content} />}>
      {/* Único innerHTML del proyecto con contenido ajeno. Lo que entra aquí sale
          de DOMPurify justo arriba, y no hay forma de llamar a este componente
          saltándoselo: por eso el saneado está dentro y no en quien lo usa. */}
      <div {...rest} class={classes()} innerHTML={html()} />
    </Show>
  )
}

export default Markdown
